"use client"

import { useTheme } from "@/context/ThemeContext"
import { cn } from "@/lib/utils"
import { useEffect, useRef, useState } from "react"

interface Star {
  x: number
  y: number
  size: number
  alpha: number
  speed: number
}

interface StarsBackgroundProps {
  className?: string
  quantity?: number
  ease?: number
  color?: string
  staticity?: number
  size?: number
}

export const StarsBackground = ({
  className,
  quantity = 400,
  ease = 50,
  color = "#ffffff",
  staticity = 50,
  size = 0.5,
}: StarsBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null)
  const [stars, setStars] = useState<Star[]>([])
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const { theme } = useTheme()

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d")
      setContext(ctx)
    }
  }, [])

  useEffect(() => {
    if (context) {
      const handleResize = () => {
        if (canvasRef.current) {
          canvasRef.current.width = window.innerWidth
          canvasRef.current.height = window.innerHeight
          initStars()
        }
      }

      const initStars = () => {
        const stars: Star[] = []
        for (let i = 0; i < quantity; i++) {
          stars.push({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            size: Math.random() * size,
            alpha: Math.random(),
            speed: Math.random() * 0.002 + 0.002,
          })
        }
        setStars(stars)
      }

      handleResize()
      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }
  }, [context, quantity, size])

  useEffect(() => {
    if (context && stars.length > 0) {
      let animationFrameId: number

      const render = () => {
        if (canvasRef.current) {
          context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
          context.fillStyle = color
          context.globalAlpha = 0.7

          stars.forEach((star, i) => {
            const x = star.x + (mousePosition.x - window.innerWidth / 2) / ease
            const y = star.y + (mousePosition.y - window.innerHeight / 2) / ease

            context.beginPath()
            context.arc(x, y, star.size, 0, 2 * Math.PI)
            context.fill()

            stars[i].alpha += star.speed

            if (stars[i].alpha > 1) {
              stars[i].alpha = 0
              stars[i].x = Math.random() * window.innerWidth
              stars[i].y = Math.random() * window.innerHeight
            }
          })
        }

        animationFrameId = window.requestAnimationFrame(render)
      }

      render()

      return () => {
        window.cancelAnimationFrame(animationFrameId)
      }
    }
  }, [context, stars, color, ease, mousePosition])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY })
    }

    window.addEventListener("mousemove", handleMouseMove)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
    }
  }, [staticity])

  return (
    <canvas
      ref={canvasRef}
      className={cn("fixed inset-0 w-full h-full", className)}
      style={{ opacity: theme === "dark" ? 0.9 : 0.5 }}
    />
  )
} 